import { Moment } from 'moment';
import type { OrganisationFunction, Team, User } from '../../../common/types/objects';

export enum EEventTypes {
  EXCHANGE = 'exchange',
  BIRTHDAY = 'birthdays',
  ANNIVERSARY = 'anniversaries',
  SHIFT = 'shift',
  EVENT = 'event'
}

export enum EApiResponseTypes {
  CALENDAR_EVENT = 'calendar_event',
  EVENT_EVENT = 'event_event',
}

export enum EEventFilterTypes {
  CELEBRATION = 'celebration',
  EVENT = 'event',
  SHIFT = 'shift',
  MY_EXCHANGE = 'My Exchanges',
  OPEN_EXCHANGE = 'Open Exchanges',
  PENDING_EXCHANGE = 'Pending Exchanges',
  APPROVED_EXCHANGE = 'Approved Exchanges',
}

export enum EEventStatus {
  OPEN = 'open',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  AWAITING_APPROVAL = 'awaiting_approval',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  PENDING = 'pending',
}

export enum CalendarActionTypes {
  NOW = 'now',
  NEXT = 'next',
  PREVIOUS = 'previous',
}

export interface BaseEvent {
  id: string;
  type: EEventTypes;
  title: string;
  date: string;
  start_time?: string;
  end_time?: string;
  description?: string;
  user?: User;
  created_at: string;
  filter_type: EEventFilterTypes | EEventFilterTypes[];
}

export interface ExchangeEvent extends BaseEvent {
  type: EEventTypes.EXCHANGE;
  shift_id?: string;
  status: EEventStatus;
  user_id: string;
  user: User;
  created_for: string;
  created_in: {
    values: Array<string>;
  };
  function_name?: string;
}

export interface BirthdayEvent extends BaseEvent {
  type: EEventTypes.BIRTHDAY;
  users: User[];
  age?: number;
}

export interface AnniversaryEvent extends BaseEvent {
  type: EEventTypes.ANNIVERSARY;
  users: User[];
  years: number;
  started_at: string;
}

export interface ShiftEvent extends BaseEvent {
  type: EEventTypes.SHIFT;
  user: User;
  break: string;
  network_name?: string;
  function_name?: string;
  location?: string;
  is_integrated: boolean;
  date: string;
  function?: OrganisationFunction;
  exchange_id?: string;
  function_id?: string;
  team_id?: string;
  team?: Team;
  start_time: string;
  end_time: string;
  description?: string;
}

export interface CompanyEvent extends BaseEvent {
  type: EEventTypes.EVENT;
  location?: string;
  attendees?: User[];
  event_category: 'meeting' | 'trip' | 'celebration' | 'training';
  is_mandatory?: boolean;
  is_full_day_event?: boolean;
  settings?: {
    disable_guest_list: boolean;
    max_guest_limit: number | null;
    only_admins_can_post: boolean;
    hide_guest_list: boolean;
    registration_deadline: string | null;
  };
  is_going?: boolean;
  status?: 'LIVE' | 'CANCELED';
}

export type CalendarEvent = ExchangeEvent | BirthdayEvent | AnniversaryEvent | ShiftEvent | CompanyEvent;

export interface CalendarDay {
  date: Moment;
  events: CalendarEvent[];
  isfirstdate: boolean;
  isToday: boolean;
  isCurrentMonth: boolean;
}

export interface CalendarWeek {
  days: CalendarDay[];
  weekNumber: number;
  startDate: Moment;
  endDate: Moment;
}

export interface CalendarContextType {
  // State
  currentWeek: Moment;
  events: CalendarEvent[];
  filteredEvents: CalendarEvent[];
  activeFilters: EEventFilterTypes[];
  statusFilter: EEventStatus | null;
  isLoading: boolean;
  error: string | null;
  refetchSchedules: boolean;
  // Actions
  goToNextWeek: () => void;
  goToPreviousWeek: () => void;
  goToToday: () => void;
  setCurrentWeek: (week: Moment) => void;
  toggleEventTypeFilter: (type: EEventFilterTypes[]) => void;
  clearFilters: () => void;
  fetchEvents: (week: Moment, organisationId?: string, loggedUserId?: string) => Promise<void>;
  addEvent: (event: CalendarEvent) => void;
  updateEvent: (eventId: string, updates: Partial<CalendarEvent>) => void;
  deleteEvent: (eventId: string) => void;
  refetchSchedulesCallback: () => void;
  setStatusFilter: (status: EEventStatus | null) => void;
}
